import type { MovieCard as MovieCardModel } from '@zyx/shared';
import { Clapperboard } from 'lucide-react';
import { EmptyState, ErrorState } from '@/components/common/states';
import { FeaturedHero } from '@/components/movie/featured-hero';
import { MovieShelf, QueryShelf } from '@/components/movie/shelf';
import { Skeleton } from '@/components/ui/skeleton';
import { useContinueWatching, useMovieList, useTerms } from '@/hooks/use-movies';
import { urls } from '@/lib/urls';

/** The newest movies, the hero's pool and the first shelf: one query, shared. */
const NEWEST = { sort: 'published', limit: 24 } as const;

/**
 * What the hero features: the movie the viewer is part-way through, so Play resumes
 * it; otherwise the newest movie that plays and has artwork.
 */
function featured(resuming: ReadonlyArray<MovieCardModel>, newest: ReadonlyArray<MovieCardModel>): MovieCardModel | undefined {
  return resuming.find((movie) => movie.cover !== undefined) ?? newest.find((movie) => movie.playable && movie.cover !== undefined) ?? newest[0];
}

export function HomePage() {
  const continueWatching = useContinueWatching();
  const newest = useMovieList(NEWEST);
  // The library's three biggest genres, each a shelf; only genres it really has.
  const genres = useTerms({ kind: 'genre', limit: 3 });
  const resuming = continueWatching.data?.movies ?? [];
  const newestMovies = newest.data?.pages[0]?.movies ?? [];
  const hero = featured(resuming, newestMovies);

  if (newest.isError) {
    return <ErrorState error={newest.error} onRetry={() => void newest.refetch()} />;
  }
  if (newest.isPending || continueWatching.isPending) {
    return (
      <div className="space-y-10">
        <Skeleton className="-mx-4 -mt-6 h-[26rem] rounded-none sm:-mx-6 sm:h-[30rem] lg:-mx-8 lg:h-[34rem]" />
        <Skeleton className="h-6 w-48" />
      </div>
    );
  }
  if (hero === undefined) {
    return (
      <EmptyState
        title="Nothing indexed yet"
        description="Start a job under Indexing, by category or a random walk, and movies appear here as they are indexed."
        icon={<Clapperboard className="size-5" />}
      />
    );
  }

  return (
    <div className="space-y-10 pb-6 sm:space-y-12">
      <FeaturedHero movie={hero} />
      {resuming.length === 0 ? null : <MovieShelf title="Continue watching" movies={resuming} link={{ to: urls.history(), label: 'History' }} />}
      <QueryShelf title="My watchlist" request={{ watchlist: true }} link={{ to: urls.watchlist(), label: 'See all' }} />
      <MovieShelf title="Newest releases" movies={newestMovies} link={{ to: urls.browse(), label: 'Browse all' }} />
      <QueryShelf title="Recently added" request={{ sort: 'added' }} link={{ to: `${urls.browse()}?sort=added`, label: 'See all' }} />
      {(genres.data?.terms ?? []).map((genre) => (
        <QueryShelf key={genre.termId} title={genre.name} request={{ termId: genre.termId }} link={{ to: urls.term(genre.termId), label: `All ${genre.movieCount.toLocaleString()}` }} />
      ))}
    </div>
  );
}
